/**
 * Le dessin du décompte (`assets/animations/start-countdown.json`), colorié au thème. Pur.
 *
 * Le JSON est la copie au design system de l'animation de l'ancien espace (`Countdown.json`), régénérée par
 * `generateStartCountdown.mjs` : les chiffres 3, 2 et 1 en Outfit 800, une face pleine sans écho, le rythme et
 * les tailles des jetons ; « C'est parti ! » est un texte du composant. Il n'écrit
 * aucune couleur : chaque aplat porte le nom de son rôle, et ce module lui donne celle du thème. Le
 * mode sombre suit donc tout seul, au natif comme au web.
 *
 * Pourquoi pas `colorFilters` de `lottie-react-native` : sa version web (`index.web.tsx`, sur
 * `@lottiefiles/dotlottie-react`) ne lit pas cette prop, le dessin y resterait noir. Un JSON colorié avant
 * d'être passé en `source` se lit pareil des deux côtés.
 */
import type { AnimationObject } from "lottie-react-native"

import source from "../../assets/animations/start-countdown.json"
import { type SemanticColorKey } from "../../theme/tokens/colors"

/**
 * Les rôles des aplats, et le jeton de chacun. Un seul orange, celui d'ÉCRITURE de l'élève, comme le
 * « C'est parti ! » en texte qui suit : les échos orange empilés sous la face de la première version
 * (l'orange d'affichage, sa tinte) faisaient une superposition d'oranges « pas très jolie en mode clair »
 * (Wajih, 20/10). Sur la nuit, l'écriture s'éclaircit : la même règle, sans écho non plus.
 *  - `face`, le chiffre (le marine de l'ancien) ;
 *  - `speed`, les traits de vitesse, à côté de la face, jamais dessous.
 */
export const START_COUNTDOWN_ROLES = {
    face: "role-strong",
    speed: "role-strong",
} as const satisfies Record<string, SemanticColorKey>

export type StartCountdownRole = keyof typeof START_COUNTDOWN_ROLES

export type StartCountdownPalette = Record<StartCountdownRole, string>

/** Le dessin tel qu'il est empaqueté, sans couleur. */
export const START_COUNTDOWN_SOURCE = source as unknown as AnimationObject

/** Le côté du dessin, en points : le composant le pose à cette taille (un point = un pixel). */
export const START_COUNTDOWN_SIZE = START_COUNTDOWN_SOURCE.w

/** Une couleur du thème (`#rgb`, `#rrggbb`, `#rrggbbaa`, `rgb()`, `rgba()`) en couleur Lottie (0 à 1). */
export function lottieColor(color: string): [number, number, number, number] | null {
    const value = color.trim()
    const hex = /^#([0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/i.exec(value)
    if (hex) {
        const digits = hex[1].length === 3 ? [...hex[1]].map((d) => d + d).join("") : hex[1]
        const channel = (index: number) => parseInt(digits.slice(index * 2, index * 2 + 2), 16) / 255
        return [channel(0), channel(1), channel(2), digits.length === 8 ? channel(3) : 1]
    }
    const rgb = /^rgba?\(\s*([\d.]+)[\s,]+([\d.]+)[\s,]+([\d.]+)(?:[\s,/]+([\d.]+%?))?\s*\)$/i.exec(value)
    if (rgb) {
        const alpha = rgb[4] == null ? 1 : rgb[4].endsWith("%") ? parseFloat(rgb[4]) / 100 : parseFloat(rgb[4])
        return [Number(rgb[1]) / 255, Number(rgb[2]) / 255, Number(rgb[3]) / 255, alpha]
    }
    return null
}

type Shape = { ty?: string; nm?: string; it?: Shape[]; c?: { a: number; k: unknown } }
type Layer = { shapes?: Shape[] }

/** Chaque aplat du dessin, avec son rôle (son nom). */
export function startCountdownFills(animation: AnimationObject = START_COUNTDOWN_SOURCE): Shape[] {
    const fills: Shape[] = []
    const walk = (shapes: Shape[] | undefined) => {
        for (const shape of shapes ?? []) {
            if (shape.ty === "fl") fills.push(shape)
            if (shape.ty === "gr") walk(shape.it)
        }
    }
    for (const layer of animation.layers as Layer[]) walk(layer.shapes)
    return fills
}

const isRole = (name: string | undefined): name is StartCountdownRole =>
    name != null && Object.prototype.hasOwnProperty.call(START_COUNTDOWN_ROLES, name)

/** Le dessin, chaque aplat à la couleur de son rôle. Une copie : la source n'est jamais touchée. */
export function themedStartCountdown(palette: StartCountdownPalette): AnimationObject {
    const animation = JSON.parse(JSON.stringify(START_COUNTDOWN_SOURCE)) as AnimationObject
    for (const fill of startCountdownFills(animation)) {
        if (!isRole(fill.nm) || fill.c == null) continue
        const color = lottieColor(palette[fill.nm])
        if (color != null) fill.c = { a: 0, k: color }
    }
    return animation
}
